'use client';

import type { PointerEvent, ReactNode } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../utils/cx';
import { mergePresetClasses } from '../utils/mergePresetClasses';

export interface DialogProps {
  open: boolean;
  onClose?: () => void;
  children: ReactNode;
  className?: string;
  position?: 'center' | 'right';
  ariaLabel?: string;
}

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const selectors = [
    'a[href]',
    'button:not([disabled])',
    'textarea:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  return Array.from(container.querySelectorAll<HTMLElement>(selectors)).filter((el) => {
    if (el.hasAttribute('disabled')) return false;
    if (el.getAttribute('aria-hidden') === 'true') return false;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    return true;
  });
}

function dialogPresetClasses(position: 'center' | 'right'): string {
  const panel =
    position === 'center'
      ? 'w-full max-w-lg rounded-lg bg-primary shadow-xl ring-1 ring-secondary_alt ring-inset'
      : 'h-full w-full max-w-md bg-primary shadow-xl ring-1 ring-secondary_alt ring-inset rounded-none';
  const anim =
    position === 'center'
      ? 'animate-in fade-in-0 zoom-in-95'
      : 'animate-in slide-in-from-right-full';
  return `${panel} ${anim}`;
}

export function Dialog({
  open,
  onClose = () => {},
  children,
  className,
  position = 'center',
  ariaLabel,
}: DialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const positionClasses = useMemo(
    () => ({
      center: 'items-center justify-center p-4',
      right: 'justify-end',
    }),
    [],
  );

  const mergedClassName = useMemo(() => {
    const preset = dialogPresetClasses(position);
    return mergePresetClasses(preset, className);
  }, [position, className]);

  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    panelRef.current?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        const currentPanel = panelRef.current;
        if (!currentPanel) return;
        const focusables = getFocusableElements(currentPanel);

        if (focusables.length === 0) {
          event.preventDefault();
          currentPanel.focus();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;

        if (!event.shiftKey && active === last) {
          event.preventDefault();
          first.focus();
        } else if (event.shiftKey && active === first) {
          event.preventDefault();
          last.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
      previouslyFocusedRef.current?.focus({ preventScroll: true });
      previouslyFocusedRef.current = null;
    };
  }, [open, onClose]);

  const handleOverlayPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.target === overlayRef.current) onClose();
  };

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      className={cx('fixed inset-0 z-50 flex bg-black/60 backdrop-blur-sm', positionClasses[position])}
      onPointerDown={handleOverlayPointerDown}
      role="presentation"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={mergedClassName}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        {children}
      </div>
</div>,
    document.body,
  );
}

Dialog.displayName = 'Dialog';